import React, { useEffect, useState } from 'react'
import ConfigPageLayout from '../components/ConfigPageLayout'
import { api } from '../lib/api'

export default function SystemPromptsConfigPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [mcpToolsInstructions, setMcpToolsInstructions] = useState('')
  const [strictRagInstructions, setStrictRagInstructions] = useState('')
  const [toolErrorMultipleOffersInstructions, setToolErrorMultipleOffersInstructions] = useState('')
  const [toolErrorGenericInstructions, setToolErrorGenericInstructions] = useState('')
  const [defaults, setDefaults] = useState<{
    mcp_tools_instructions: string
    strict_rag_instructions: string
    tool_error_multiple_offers_instructions: string
    tool_error_generic_instructions: string
  } | null>(null)

  useEffect(() => {
    void loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)

      const [
        { data: mcpData },
        { data: ragData },
        { data: multiOffersData },
        { data: genericToolErrorData },
      ] = await Promise.all([
        api.get('/admin/settings/mcp_tools_instructions'),
        api.get('/admin/settings/strict_rag_instructions'),
        api.get('/admin/settings/tool_error_multiple_offers_instructions'),
        api.get('/admin/settings/tool_error_generic_instructions'),
      ])

      let defaultData: {
        mcp_tools_instructions: string
        strict_rag_instructions: string
        tool_error_multiple_offers_instructions: string
        tool_error_generic_instructions: string
      } | null = null
      try {
        const { data } = await api.get('/admin/settings/prompt-defaults')
        defaultData = data
      } catch {
        // Optional endpoint: fallback to currently loaded values
      }

      const mcpValue = mcpData?.value ?? ''
      const ragValue = ragData?.value ?? ''
      const resolvedDefaults = defaultData ?? {
        mcp_tools_instructions: mcpValue,
        strict_rag_instructions: ragValue,
        tool_error_multiple_offers_instructions: multiOffersData?.value ?? '',
        tool_error_generic_instructions: genericToolErrorData?.value ?? '',
      }

      setDefaults(resolvedDefaults)
      setMcpToolsInstructions(mcpValue || resolvedDefaults.mcp_tools_instructions || '')
      setStrictRagInstructions(ragValue || resolvedDefaults.strict_rag_instructions || '')
      setToolErrorMultipleOffersInstructions(
        multiOffersData?.value || resolvedDefaults.tool_error_multiple_offers_instructions || ''
      )
      setToolErrorGenericInstructions(
        genericToolErrorData?.value || resolvedDefaults.tool_error_generic_instructions || ''
      )
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Impossible de charger les prompts systeme.')
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      await Promise.all([
        api.put('/admin/settings/mcp_tools_instructions', {
          value: mcpToolsInstructions,
          value_type: 'string',
          description: "Instructions globales pour l'utilisation des outils MCP par les agents",
        }),
        api.put('/admin/settings/strict_rag_instructions', {
          value: strictRagInstructions,
          value_type: 'string',
          description: 'Instructions globales du mode RAG strict',
        }),
        api.put('/admin/settings/tool_error_multiple_offers_instructions', {
          value: toolErrorMultipleOffersInstructions,
          value_type: 'string',
          description: 'Message metier quand plusieurs offres correspondent (sans erreur technique brute)',
        }),
        api.put('/admin/settings/tool_error_generic_instructions', {
          value: toolErrorGenericInstructions,
          value_type: 'string',
          description: 'Message metier generique pour les erreurs outil (sans details techniques)',
        }),
      ])

      setSuccess('Prompts systeme enregistres avec succes.')
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Echec de l'enregistrement des prompts systeme.")
    } finally {
      setSaving(false)
    }
  }

  const resetField = (field: 'mcp' | 'strict' | 'multiOffers' | 'genericToolError') => {
    if (!defaults) return
    if (field === 'mcp') {
      setMcpToolsInstructions(defaults.mcp_tools_instructions)
      return
    }
    if (field === 'strict') {
      setStrictRagInstructions(defaults.strict_rag_instructions)
      return
    }
    if (field === 'multiOffers') {
      setToolErrorMultipleOffersInstructions(defaults.tool_error_multiple_offers_instructions)
      return
    }
    setToolErrorGenericInstructions(defaults.tool_error_generic_instructions)
  }

  return (
    <ConfigPageLayout
      title="PROMPTS SYSTEME"
      description="Editez les prompts globaux appliques aux agents (MCP et mode RAG strict)."
    >
      <div className="rounded-3xl border border-emerald-100/80 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
            Chargement des prompts systeme...
          </div>
        ) : (
          <div className="space-y-8">
            <div className="rounded-2xl border border-blue-200/70 bg-blue-50 p-6 dark:border-blue-900/60 dark:bg-blue-950/40">
              <div className="flex items-start gap-4">
                <svg className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100">Prompts globaux</h3>
                  <p className="mt-2 text-sm text-blue-700 dark:text-blue-200">
                    Ces instructions sont partagees par tous les agents concernes. Toute modification impacte
                    immediatement le comportement global des reponses.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">MCP Tools Instructions</h3>
                <button
                  type="button"
                  onClick={() => resetField('mcp')}
                  className="rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-xs font-medium text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  Restaurer defaut
                </button>
              </div>
              <textarea
                value={mcpToolsInstructions}
                onChange={(e) => setMcpToolsInstructions(e.target.value)}
                rows={16}
                className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:ring-emerald-800"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Tool Error - Plusieurs Offres</h3>
                <button
                  type="button"
                  onClick={() => resetField('multiOffers')}
                  className="rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-xs font-medium text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  Restaurer defaut
                </button>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Utilise le placeholder <code>{'{offers}'}</code> pour injecter automatiquement la liste des offres correspondantes.
              </p>
              <textarea
                value={toolErrorMultipleOffersInstructions}
                onChange={(e) => setToolErrorMultipleOffersInstructions(e.target.value)}
                rows={10}
                className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:ring-emerald-800"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Tool Error - Generique</h3>
                <button
                  type="button"
                  onClick={() => resetField('genericToolError')}
                  className="rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-xs font-medium text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  Restaurer defaut
                </button>
              </div>
              <textarea
                value={toolErrorGenericInstructions}
                onChange={(e) => setToolErrorGenericInstructions(e.target.value)}
                rows={8}
                className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:ring-emerald-800"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Strict RAG Instructions</h3>
                <button
                  type="button"
                  onClick={() => resetField('strict')}
                  className="rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-xs font-medium text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  Restaurer defaut
                </button>
              </div>
              <textarea
                value={strictRagInstructions}
                onChange={(e) => setStrictRagInstructions(e.target.value)}
                rows={20}
                className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:ring-emerald-800"
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
                {success}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={saveSettings}
                disabled={saving}
                className="rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-emerald-500 dark:hover:bg-emerald-600"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer les prompts'}
              </button>
              <button
                type="button"
                onClick={() => void loadSettings()}
                disabled={saving}
                className="rounded-full border border-zinc-200/70 bg-white/80 px-6 py-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                Recharger
              </button>
            </div>
          </div>
        )}
      </div>
    </ConfigPageLayout>
  )
}
